import { Box, useTheme } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import embed from 'vega-embed'
import type { ChartPayload } from '../../shared/types'

export default function ChartBlock({ chart }: { chart: ChartPayload }) {
  const ref = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'

  useEffect(() => {
    if (chart.kind !== 'vega') { return undefined }
    const el = ref.current
    if (!el) { return undefined }
    let result: { finalize?: () => void } | undefined
    let ro: ResizeObserver | undefined
    let timer = 0
    let lastWidth = -1
    const responsiveSpec = {
      ...(chart.spec as Record<string, unknown>),
      width: 'container',
    }
    const renderChart = () => {
      if (result && result.finalize) { try { result.finalize() } catch (e) {} }
      el.innerHTML = ''
      embed(el, responsiveSpec as any, {
        actions: false,
        renderer: 'svg',
        theme: dark ? 'dark' : 'vox',
        config: { background: 'transparent', mark: { tooltip: true } },
        tooltip: { theme: dark ? 'dark' : 'light' },
      })
        .then((r: { finalize?: () => void }) => { result = r })
        .catch(() => {})
    }
    renderChart()
    if (typeof ResizeObserver !== 'undefined') {
      lastWidth = el.clientWidth
      ro = new ResizeObserver(() => {
        const w = el.clientWidth
        if (Math.abs(w - lastWidth) < 16) { return }
        window.clearTimeout(timer)
        timer = window.setTimeout(() => { lastWidth = w; renderChart() }, 160)
      })
      ro.observe(el)
    }
    return () => {
      if (ro) { ro.disconnect() }
      window.clearTimeout(timer)
      result?.finalize?.()
    }
  }, [chart, dark])

  if (chart.kind === 'svg') {
    return (
      <Box
        sx={{
          my: 0.75,
          p: 1,
          borderRadius: 1.5,
          bgcolor: dark ? '#fff' : 'transparent',
          border: dark ? 'none' : 1,
          borderColor: 'divider',
        }}
      >
        <img
          data-testid="ca-chart-svg"
          alt="chart"
          style={{ width: '100%', height: 'auto', display: 'block' }}
          src={`data:image/svg+xml;utf8,${encodeURIComponent(chart.svg)}`}
        />
      </Box>
    )
  }
  return (
    <Box sx={{ my: 0.75, width: '100%', '& .vega-embed': { width: '100%' } }}>
      <div ref={ref} data-testid="ca-chart" style={{ width: '100%' }} />
    </Box>
  )
}
